if (!customElements.get('licence-selector')) {
  customElements.define('licence-selector', class LicenceSelector extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.cards = Array.from(this.querySelectorAll('[data-licence-group]'));
      this.variants = [];

      try {
        this.variants = JSON.parse(this.querySelector('[data-licence-variants]').textContent);
      } catch (error) {
        console.error('Unable to read licence variants', error);
      }

      this.sourceVariantId = Number(this.dataset.sourceVariantId || 0);

      this.cards.forEach((card) => {
        const toggle = card.querySelector('[data-licence-toggle]');
        const select = card.querySelector('[data-licence-select]');

        if (toggle && !toggle.disabled) {
          toggle.addEventListener('change', () => {
            this.updateCard(card);
            this.syncSelection();
          }, { signal: this.controller.signal });
        }

        if (select) {
          select.addEventListener('change', () => {
            this.updateCard(card);
            this.syncSelection();
          }, { signal: this.controller.signal });
        }

        this.updateCard(card);
      });

      this.form = this.closest('product-information')?.querySelector('product-form form[action*="/cart/add"]');
      this.submitButton = this.form?.querySelector('button[type="submit"][name="add"]');
      this.addToCartText = this.submitButton?.querySelector('[data-add-to-cart-text]');
      this.loadingIcon = this.submitButton?.querySelector('[data-loading-icon]');

      if (this.form) {
        this.form.addEventListener('submit', (event) => this.addSelectedLicences(event), {
          capture: true,
          signal: this.controller.signal
        });
      }

      this.syncSelection();
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
    }

    updateCard(card) {
      const toggle = card.querySelector('[data-licence-toggle]');
      const select = card.querySelector('[data-licence-select]');
      const details = card.querySelector('[data-licence-details]');
      const enabled = toggle?.disabled || Boolean(toggle?.checked);

      card.classList.toggle('is-selected', enabled);
      if (toggle) toggle.setAttribute('aria-expanded', String(enabled));
      if (details) details.hidden = !enabled;
      if (select) select.disabled = !enabled;

      const option = select?.options[select.selectedIndex];
      const price = card.querySelector('[data-licence-price]');
      if (option && price) price.textContent = option.dataset.money || '';
    }

    selectedItems() {
      return this.cards.flatMap((card) => {
        const toggle = card.querySelector('[data-licence-toggle]');
        const select = card.querySelector('[data-licence-select]');
        const enabled = toggle?.disabled || Boolean(toggle?.checked);
        if (!enabled || !select?.value) return [];

        const option = select.options[select.selectedIndex];
        const licenceGroup = card.dataset.licenceGroup;
        const item = { id: Number(select.value), quantity: 1 };

        if (licenceGroup === 'Desktop') {
          const tier = option?.dataset.licenceTier || '1 user';

          if (option?.dataset.licenceUpgrade === 'true' && this.sourceVariantId) {
            return [
              {
                id: this.sourceVariantId,
                quantity: 1,
                properties: {
                  _licence_type: 'Desktop',
                  _licence_users: tier
                }
              },
              {
                id: item.id,
                quantity: 1,
                properties: {
                  _licence_upgrade: tier,
                  _source_variant_id: String(this.sourceVariantId)
                }
              }
            ];
          }

          item.properties = {
            _licence_type: 'Desktop',
            _licence_users: tier
          };
        } else {
          item.properties = {
            _licence_type: licenceGroup,
            _licence_term: '1 year',
            _renewal_required: 'true'
          };
        }

        return [item];
      });
    }

    selectedTotal() {
      return this.selectedItems().reduce((total, item) => {
        const variant = this.variants.find((candidate) => Number(candidate.id) === item.id);
        return total + Number(variant?.price || 0);
      }, 0);
    }

    formatMoney(cents) {
      const currency = window.Shopify?.currency?.active || 'GBP';
      const locale = document.documentElement.lang || 'en-GB';
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 2
        }).format(cents / 100);
      } catch (error) {
        return '£' + (cents / 100).toFixed(2);
      }
    }

    syncSelection() {
      const items = this.selectedItems();
      const total = this.selectedTotal();
      const money = this.formatMoney(total);
      const totalOutput = this.querySelector('[data-licence-total]');
      const productInformation = this.closest('product-information');
      const mainPrice = productInformation?.querySelector('[data-product-price]');
      const mainPriceText = mainPrice?.querySelector('[data-price-text]');
      const desktopSelect = this.querySelector('[data-licence-select="Desktop"]');
      const hiddenVariantInput = this.form?.querySelector('input[name="id"]');

      if (totalOutput) totalOutput.textContent = money;
      if (mainPriceText) mainPriceText.textContent = money;
      if (mainPrice) mainPrice.setAttribute('content', (total / 100).toFixed(2));
      if (hiddenVariantInput && this.sourceVariantId) hiddenVariantInput.value = this.sourceVariantId;
      if (this.addToCartText && !this.adding) this.addToCartText.textContent = 'Add to cart — ' + money;
      if (this.submitButton) this.submitButton.disabled = items.length === 0 || this.adding;
    }

    setLoading(loading) {
      this.adding = loading;
      if (this.submitButton) this.submitButton.disabled = loading;
      if (this.loadingIcon) this.loadingIcon.classList.toggle('hidden', !loading);
      if (this.addToCartText) {
        this.addToCartText.textContent = loading ? 'Adding licences…' : 'Add to cart — ' + this.formatMoney(this.selectedTotal());
      }
    }

    showError(message) {
      const error = this.querySelector('[data-licence-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    }

    async addSelectedLicences(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.adding) return;

      const items = this.selectedItems();
      if (!items.length) return;

      this.showError('');
      this.setLoading(true);

      try {
        const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: items })
        });

        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'The selected licences could not be added.');

        window.location.assign(this.dataset.cartUrl || '/cart');
      } catch (error) {
        this.showError(error.message || 'Something went wrong. Please try again.');
        this.setLoading(false);
        this.syncSelection();
      }
    }
  });
}